






















































































































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtInt, fmtFloat } from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import HelpIcon from "./HelpIcon.vue";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any;
}

function newPanelState(): PanelState {
    return { loading: false, error: null, data: null };
}

interface LinkOption {
    link_id: number;
    road_name: string;
    label: string;
}

@Component({ components: { HelpIcon } })
export default class SelectedExposurePanel extends Vue {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    @Prop({ required: true }) scenarioDate!: string;
    /** Catalog payload — currently only used to keep prop parity with the parent. */
    @Prop({ default: null }) catalog!: any;
    /** Global sidebar filters — narrow selected-exposure facts to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;

    /** Help text shown in the ?-tooltip beside the Link / Road-name selection radio. */
    selectionHelp =
        "Choose whether to drill into a single closed road segment or every closed road segment sharing the same road name. A road-name selection combines all of that road's closed road segments for the selected day, and counts each trip only once.";
    /**
     * The map rows already filtered by the parent's state filter — this is the source of the
     * Link / Road dropdown options so the user can only pick links visible on the map.
     */
    @Prop({ default: () => [] }) mapRows!: any[];
    /**
     * Most-recently clicked map row (from a marker click or a table drill button). When this
     * changes, we apply the field that matches the current selection mode — so clicking a
     * marker while in "Road name" mode picks that link's road, not its link_id.
     */
    @Prop({ default: null }) clickedRow!: {
        link_id: number;
        road_name: string;
    } | null;

    fmtInt = fmtInt;
    fmtFloat = fmtFloat;

    selectionLevel: "link_id" | "road_name" = "link_id";
    linkSelection: number | null = null;
    roadSelection: string | null = null;

    summary: PanelState = newPanelState();
    commodity: PanelState = newPanelState();
    enterpriseTypes: PanelState = newPanelState();

    /** True while any of the panel's fetches is in flight — disables the selection controls. */
    get panelLoading(): boolean {
        return (
            this.summary.loading ||
            this.commodity.loading ||
            this.enterpriseTypes.loading
        );
    }

    mounted() {
        this.applyClickedRow();
        this.fetchAll();
    }

    @Watch("clickedRow")
    onClickedRowChanged() {
        this.applyClickedRow();
        this.fetchAll();
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${
            this.scenarioDate
        }|${JSON.stringify(this.filters)}`;
    }

    @Watch("fetchParamsKey")
    onDateContextChanged() {
        this.fetchAll();
    }

    @Watch("mapRows", { deep: false })
    onMapRowsChanged() {
        // If the user's current selection is still visible, leave it alone — that respects an
        // explicit pick the user made. If it's been filtered out, fall back to the FIRST ROW of
        // the visible map data — which is also the first row in the Closed Links table (both
        // ordered by impact score from the SQL). We deliberately don't use linkOptions[0] /
        // roadOptions[0] because those are sorted alphabetically for the dropdown UX.
        const firstRow = this.mapRows?.[0] ?? null;
        if (
            this.selectionLevel === "link_id" &&
            this.linkSelection != null &&
            !this.linkOptions.some((o) => o.link_id === this.linkSelection)
        ) {
            this.linkSelection = firstRow?.link_id ?? null;
            this.fetchAll();
        }
        if (
            this.selectionLevel === "road_name" &&
            this.roadSelection &&
            !this.roadOptions.includes(this.roadSelection)
        ) {
            this.roadSelection = firstRow?.road_name ?? null;
            this.fetchAll();
        }
    }

    /**
     * Apply the most recent map click (or table drill) to whichever field matches the current
     * selection mode. Clicking a marker while in "Road name" mode therefore selects that link's
     * road, not its link_id — and vice versa.
     */
    applyClickedRow() {
        if (!this.clickedRow) return;
        if (this.selectionLevel === "link_id") {
            this.linkSelection = this.clickedRow.link_id;
        } else {
            this.roadSelection = this.clickedRow.road_name || null;
        }
    }

    /**
     * Toggling Link ↔ Road name should keep showing the *same* underlying selection, just at
     * a different granularity. So instead of falling back to the first dropdown option, we
     * derive the new mode's value from the current one.
     */
    onSelectionLevelChange() {
        if (this.selectionLevel === "road_name") {
            // Use the road_name of the currently-selected link (preferred), or of the most
            // recently clicked row, or finally the first available road.
            const fromLink = this.mapRows.find(
                (r) => r.link_id === this.linkSelection,
            );
            const candidate =
                fromLink?.road_name ||
                this.clickedRow?.road_name ||
                this.roadOptions[0] ||
                null;
            this.roadSelection = candidate;
        } else {
            // Use the first link_id whose road matches the currently-selected road, or the most
            // recently clicked link, or finally the first available link.
            let candidate: number | null = null;
            if (this.roadSelection) {
                const match = this.mapRows.find(
                    (r) => r.road_name === this.roadSelection,
                );
                if (match) candidate = match.link_id;
            }
            if (candidate == null && this.clickedRow) {
                candidate = this.clickedRow.link_id;
            }
            if (candidate == null && this.linkOptions.length) {
                candidate = this.linkOptions[0].link_id;
            }
            this.linkSelection = candidate;
        }
        this.fetchAll();
    }

    @Watch("linkSelection")
    @Watch("roadSelection")
    onSelectionValueChanged() {
        this.fetchAll();
    }

    get linkOptions(): LinkOption[] {
        const seen = new Set<number>();
        const out: LinkOption[] = [];
        for (const r of this.mapRows) {
            if (r.link_id == null || seen.has(r.link_id)) continue;
            seen.add(r.link_id);
            const road = r.road_name || "(unnamed road)";
            out.push({
                link_id: r.link_id,
                road_name: road,
                label: `${road} (link_id=${r.link_id})`,
            });
        }
        out.sort((a, b) => a.label.localeCompare(b.label));
        return out;
    }

    get roadOptions(): string[] {
        const seen = new Set<string>();
        for (const r of this.mapRows) {
            if (r.road_name) seen.add(r.road_name);
        }
        return Array.from(seen).sort();
    }

    get hasSelection(): boolean {
        return this.selectionLevel === "link_id"
            ? this.linkSelection != null
            : !!this.roadSelection;
    }

    get currentSelectionValue(): number | string | null {
        return this.selectionLevel === "link_id"
            ? this.linkSelection
            : this.roadSelection;
    }

    get summaryRow(): any {
        const data = this.summary.data;
        return Array.isArray(data) && data.length ? data[0] : null;
    }

    get summaryKpis() {
        const r = this.summaryRow;
        if (!r) return [];
        return [
            {
                key: "baseline_trips",
                label: "Baseline trips *",
                tooltip:
                    "Trips whose baseline route used the selected closed road segment/road.",
                value: fmtInt(r.baseline_exposed_input_trip_count),
            },
            {
                key: "baseline_tonnes",
                label: "Baseline tonnes *",
                tooltip:
                    "Tonnes carried by the baseline trips, scaled to the scenario window.",
                value: `${fmtFloat(r.baseline_exposed_input_tonnes, 1)} t`,
            },
            {
                key: "impacted_trips",
                label: "Impacted trips *",
                tooltip:
                    "Baseline trips that were impacted in the closure scenario.",
                value: fmtInt(r.impacted_exposed_trip_count),
            },
            {
                key: "impacted_tonnes",
                label: "Impacted tonnes *",
                tooltip:
                    "Tonnes carried by impacted trips, scaled to the scenario window.",
                value: `${fmtFloat(r.impacted_exposed_tonnes, 1)} t`,
            },
            {
                key: "rerouted_trips",
                label: "Rerouted trips *",
                tooltip:
                    "Impacted trips that were re-routed (took a detour rather than being blocked).",
                value: fmtInt(r.rerouted_exposed_trip_count),
            },
            {
                key: "rerouted_tonnes",
                label: "Rerouted tonnes *",
                tooltip: "Tonnes on impacted rerouted trips.",
                value: `${fmtFloat(r.rerouted_exposed_tonnes, 1)} t`,
            },
            {
                key: "blocked_trips",
                label: "Blocked trips *",
                tooltip:
                    "Impacted trips that could not be re-routed (blocked).",
                value: fmtInt(r.blocked_exposed_trip_count),
            },
            {
                key: "blocked_tonnes",
                label: "Blocked tonnes *",
                tooltip: "Tonnes on impacted blocked trips.",
                value: `${fmtFloat(r.blocked_exposed_tonnes, 1)} t`,
            },
        ];
    }

    get deltaKpis() {
        const r = this.summaryRow;
        if (!r) return [];
        return [
            {
                key: "selected_links",
                label: "Selected closed road segments",
                tooltip:
                    "Number of closed road segments covered by this selection on the map date.",
                value: fmtInt(r.selected_link_count),
            },
            {
                key: "road_cost",
                label: "Total extra transport cost *",
                tooltip:
                    "Modelled scenario-vs-baseline road cost delta for the selected trips.",
                value: `$${fmtFloat(r.road_delta_total_cost, 0)}`,
            },
            {
                key: "road_time",
                label: "Total extra transport hours *",
                tooltip: "Scenario-vs-baseline road time delta in hours.",
                value: `${fmtFloat(r.road_delta_time_hours, 1)} hrs`,
            },
            {
                key: "road_distance",
                label: "Total extra transport kilometres *",
                tooltip:
                    "Scenario-vs-baseline road distance delta in kilometres.",
                value: `${fmtFloat(r.road_delta_length_km, 0)} km`,
            },
        ];
    }

    fetchAll() {
        if (!this.hasSelection || !this.scenarioDate) return;
        const value = this.currentSelectionValue;
        if (value == null) return;
        const baseParams = {
            start_date: this.startDate,
            end_date: this.endDate,
            scenario_date: this.scenarioDate,
            selection_type: this.selectionLevel,
            selection_value: value,
        };
        this.fetchPanel(
            this.summary,
            endpoints.roadClosureAnalyticsSelectedExposureSummaryUrl,
            baseParams,
        );
        const drillLimit = 25;
        this.fetchPanel(
            this.commodity,
            endpoints.roadClosureAnalyticsSelectedExposureCommodityUrl,
            { ...baseParams, limit: drillLimit },
        );
        this.fetchPanel(
            this.enterpriseTypes,
            endpoints.roadClosureAnalyticsSelectedExposureEnterpriseTypeUrl,
            { ...baseParams, limit: drillLimit },
        );
    }

    fetchPanel(panel: PanelState, url: string, params: Record<string, any>) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(url, params, this.filters),
        })
            .then((response: any) => {
                panel.data = response.data ?? null;
            })
            .catch((err: any) => {
                console.error("[RCA SelectedExposure]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = null;
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    // Columns below correspond to per-commodity / per-enterprise-type instances of the
    // selected_* catalog metrics. The header text matches the SelectedExposurePanel KPI
    // tile labels (which are the canonical display_label_web values in metrics_catalog.json),
    // so a user reading the Definitions tab can match a table column to a definition without
    // mental translation. " *" follows the prototype's label_for_metric() convention for
    // scenario_window_metric=true entries and is explained by the ScenarioWindowFootnote
    // at the bottom of the Daily Map tab.
    commodityHeaders = [
        { text: "Commodity", value: "commodity" },
        {
            text: "Baseline trips *",
            value: "baseline_exposed_input_trip_count",
            align: "end",
        },
        {
            text: "Baseline tonnes *",
            value: "baseline_exposed_input_tonnes",
            align: "end",
        },
        {
            text: "Impacted trips *",
            value: "impacted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Impacted tonnes *",
            value: "impacted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted trips *",
            value: "rerouted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Rerouted tonnes *",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Blocked trips *",
            value: "blocked_exposed_trip_count",
            align: "end",
        },
        {
            text: "Blocked tonnes *",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
        {
            text: "Total extra transport cost *",
            value: "road_delta_total_cost",
            align: "end",
        },
        {
            text: "Total extra transport hours *",
            value: "road_delta_time_hours",
            align: "end",
        },
        {
            text: "Total extra transport kilometres *",
            value: "road_delta_length_km",
            align: "end",
        },
    ];

    enterpriseTypeHeaders = [
        { text: "Type", value: "enterprise_type" },
        { text: "Category", value: "enterprise_category" },
        {
            text: "Baseline trips *",
            value: "baseline_exposed_trip_count",
            align: "end",
        },
        {
            text: "Baseline tonnes *",
            value: "baseline_exposed_tonnes",
            align: "end",
        },
        {
            text: "Impacted trips *",
            value: "impacted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Impacted tonnes *",
            value: "impacted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted trips *",
            value: "rerouted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Rerouted tonnes *",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Blocked trips *",
            value: "blocked_exposed_trip_count",
            align: "end",
        },
        {
            text: "Blocked tonnes *",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
    ];

    get commodityRows(): any[] {
        return Array.isArray(this.commodity.data) ? this.commodity.data : [];
    }
    get enterpriseTypeRows(): any[] {
        return Array.isArray(this.enterpriseTypes.data)
            ? this.enterpriseTypes.data
            : [];
    }
    get originEnterpriseTypes(): any[] {
        return this.enterpriseTypeRows.filter(
            (r) => r.enterprise_role === "origin",
        );
    }
    get destinationEnterpriseTypes(): any[] {
        return this.enterpriseTypeRows.filter(
            (r) => r.enterprise_role === "destination",
        );
    }
}
